import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Typography, Button, Box, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
  Chip, MenuItem, Select, FormControl, InputLabel,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const API = process.env.REACT_APP_API_URL;

export default function Proyectos() {
  const { usuario } = useAuth();
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [horasRegistradas, setHorasRegistradas] = useState({});
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    cliente_id: '',
    monto: 0,
    horas_estimadas: 0,
    acceso: 'publico',
    owner_id: usuario?.id
  });

  const cargarDatos = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/api/proyectos`, { params: { empresa_id: usuario?.empresa_id } }),
        axios.get(`${API}/api/clientes`, { params: { empresa_id: usuario?.empresa_id } })
      ]);
      const proyectosData = Array.isArray(pRes.data) ? pRes.data : [];
      const clientesData = Array.isArray(cRes.data) ? cRes.data : [];
      setProyectos(proyectosData);
      setClientes(clientesData);
      const horas = {};
      await Promise.all(proyectosData.map(async p => {
        try {
          const res = await axios.get(`${API}/api/proyectos/${p.id}/horas`);
          horas[p.id] = res.data.horas_registradas || 0;
        } catch {
          horas[p.id] = 0;
        }
      }));
      setHorasRegistradas(horas);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setProyectos([]);
      setClientes([]);
    }
  };

  useEffect(() => { cargarDatos(); }, []);

  const abrirCrear = () => {
    setEditando(null);
    setForm({
      nombre: '',
      descripcion: '',
      cliente_id: '',
      monto: 0,
      horas_estimadas: 0,
      acceso: 'publico',
      owner_id: usuario?.id
    });
    setOpen(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({
      nombre: p.nombre,
      descripcion: p.descripcion || '',
      cliente_id: p.cliente_id || '',
      monto: p.monto || 0,
      horas_estimadas: p.horas_estimadas || 0,
      acceso: p.acceso || 'publico',
      owner_id: p.owner_id
    });
    setOpen(true);
  };

  const guardar = async () => {
    const datos = {
      ...form,
      cliente_id: form.cliente_id || null,
      owner_id: form.owner_id || null,
      empresa_id: usuario?.empresa_id || null,
      monto: parseFloat(form.monto) || 0,
      horas_estimadas: parseFloat(form.horas_estimadas) || 0
    };
    if (editando) {
      await axios.put(`${API}/api/proyectos/${editando.id}`, datos);
    } else {
      await axios.post(`${API}/api/proyectos`, datos);
    }
    setOpen(false);
    cargarDatos();
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Eliminar este proyecto y todas sus tareas y registros de tiempo?')) {
      await axios.delete(`${API}/api/proyectos/${id}`);
      cargarDatos();
    }
  };

  const getProgress = (p) => {
    if (!p.horas_estimadas || p.horas_estimadas === 0) return null;
    const registradas = horasRegistradas[p.id] || 0;
    return Math.min(Math.round((registradas / p.horas_estimadas) * 100), 100);
  };

  const getNombreCliente = (id) => {
    const c = clientes.find(c => c.id === id);
    return c ? c.nombre : '—';
  };

  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'super_admin';

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Proyectos</Typography>
        {esAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCrear}>
            Nuevo Proyecto
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Nombre</b></TableCell>
              <TableCell><b>Cliente</b></TableCell>
              <TableCell><b>Tracked</b></TableCell>
              {esAdmin && <TableCell><b>Monto</b></TableCell>}
              <TableCell><b>Progress</b></TableCell>
              <TableCell><b>Acceso</b></TableCell>
              <TableCell><b>Acciones</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proyectos.map(p => {
              const progress = getProgress(p);
              const horas = horasRegistradas[p.id] || 0;
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <Chip label={p.nombre} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>{getNombreCliente(p.cliente_id)}</TableCell>
                  <TableCell>{horas}h</TableCell>
                  {esAdmin && (
                    <TableCell>
                      {p.monto > 0 ? `$${p.monto.toLocaleString()} USD` : '—'}
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: 150 }}>
                    {progress !== null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                          color={progress >= 100 ? 'error' : progress >= 75 ? 'warning' : 'success'}
                        />
                        <Typography variant="body2">{progress}%</Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.acceso}
                      color={p.acceso === 'publico' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {esAdmin && (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => abrirEditar(p)}
                        >
                          Editar
                        </Button>
                      )}
                      {esAdmin && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => eliminar(p.id)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {proyectos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay proyectos aún
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>{editando ? 'Editar Proyecto' : 'Nuevo Proyecto'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Nombre" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth label="Descripción" value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })}
            multiline rows={2} sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Cliente</InputLabel>
            <Select
              value={form.cliente_id}
              label="Cliente"
              onChange={e => setForm({ ...form, cliente_id: e.target.value })}
            >
              <MenuItem value="">— Sin cliente —</MenuItem>
              {clientes.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {esAdmin && (
            <TextField
              fullWidth label="Monto (USD)" type="number" value={form.monto}
              onChange={e => setForm({ ...form, monto: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth label="Horas estimadas" type="number" value={form.horas_estimadas}
            onChange={e => setForm({ ...form, horas_estimadas: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Acceso</InputLabel>
            <Select
              value={form.acceso}
              label="Acceso"
              onChange={e => setForm({ ...form, acceso: e.target.value })}
            >
              <MenuItem value="publico">Público</MenuItem>
              <MenuItem value="privado">Privado</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>
            {editando ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}